import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchGiveaways, fetchGiveawaysUpcoming } from "../api/client";
import { priceView, ru } from "../lib/pricing";
import styles from "./HomePage.module.css";

/** Не показывать на главной демо-раздачи по подстроке в названии. */
function isDemoGiveawayTitle(title) {
  const t = String(title ?? "").toLowerCase();
  return (
    t.includes("демо") ||
    t.includes("демонстрацион") ||
    t.includes("демонастрацион")
  );
}

/** "2026-09-29" → "Откроется 29.09". Формат как во вкладке «Скоро» на iOS/Android. */
function formatOpenLabel(iso) {
  const parts = String(iso ?? "").split("-");
  if (parts.length !== 3) return "Скоро";
  const [, month, day] = parts;
  if (!day || !month) return "Скоро";
  return `Откроется ${day}.${month}`;
}

function GiveawayCard({ item, soon }) {
  const { finalLabel, refund } = priceView(item);
  return (
    <li>
      <Link to={`/giveaway/${item.id}`} className={styles.card}>
        <div className={styles.thumbWrap}>
          <img src={item.image} alt="" className={styles.thumb} loading="lazy" />
          {soon ? (
            <span className={styles.soonBadge}>{formatOpenLabel(item.nextOpenDate)}</span>
          ) : item.spotsLeft < 10 ? (
            <span className={styles.scarceBadge}>Мало мест</span>
          ) : null}
        </div>
        <div className={styles.cardBody}>
          <div className={styles.priceRow}>
            <span className={styles.price}>{finalLabel}</span>
            {refund > 0 ? (
              <span className={styles.cashback}>+{ru(refund)} ₽ кэшбек</span>
            ) : null}
          </div>
          <h3 className={styles.cardTitle}>{item.title}</h3>
        </div>
      </Link>
    </li>
  );
}

export default function HomePage() {
  const [current, setCurrent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [section, setSection] = useState("now"); // "now" | "soon"
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // «На раздаче» — открытые сейчас; «Скоро» — те же данные, но откроются
        // в ближайшие дни. Раньше грузили только первое, поэтому мини-апп показывала
        // меньше товаров, чем приложения. Если «Скоро» не отдалось — не роняем экран.
        const [nowList, soonList] = await Promise.all([
          fetchGiveaways(),
          fetchGiveawaysUpcoming().catch(() => []),
        ]);
        if (cancelled) return;
        const cur = nowList.filter((g) => !isDemoGiveawayTitle(g.title));
        const soon = soonList.filter((g) => !isDemoGiveawayTitle(g.title));
        setCurrent(cur);
        setUpcoming(soon);
        // Если сейчас открытых нет, а «Скоро» есть — сразу открыть эту вкладку,
        // чтобы экран не выглядел пустым (паритет с iOS/Android).
        if (cur.length === 0 && soon.length > 0) setSection("soon");
      } catch (e) {
        if (!cancelled) setError(e.message ?? "Ошибка загрузки");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const list = section === "soon" ? upcoming : current;
  // Вкладку «Скоро» показываем всегда (как просили): даже если будущих раздач
  // сейчас нет — во вкладке будет «Пока нет запланированных раздач».
  const showTabs = !loading && !error;

  return (
    <div>
      <Link
        to="/"
        className={styles.brandBar}
        aria-label="Wicoop — маркетплейс кэшбеков, на главную"
      >
        <img src="/logo.svg" alt="" className={styles.brandBarLogo} width={40} height={40} />
        <span className={styles.brandBarText}>
          <span className={styles.brandBarTitle}>Wicoop</span>
          <span className={styles.brandBarSub}>Маркетплейс кэшбеков</span>
        </span>
      </Link>

      {showTabs && (
        <div className={styles.tabs} role="tablist" aria-label="Раздачи">
          <button
            type="button"
            role="tab"
            aria-selected={section === "now"}
            className={section === "now" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setSection("now")}
          >
            На раздаче{current.length > 0 ? ` · ${current.length}` : ""}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === "soon"}
            className={section === "soon" ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setSection("soon")}
          >
            Скоро{upcoming.length > 0 ? ` · ${upcoming.length}` : ""}
          </button>
        </div>
      )}

      {loading && <p className={styles.status}>Загрузка…</p>}
      {error && <p className={styles.error}>{error}</p>}
      {!loading && !error && list.length === 0 && (
        <p className={styles.status}>
          {section === "soon"
            ? "Пока нет запланированных раздач."
            : upcoming.length > 0
              ? "Сегодня все раздачи разобрали — новые уже во вкладке «Скоро»."
              : "Пока нет активных раздач."}
        </p>
      )}

      <ul className={styles.grid}>
        {list.map((item) => (
          <GiveawayCard key={item.id} item={item} soon={section === "soon"} />
        ))}
      </ul>
    </div>
  );
}
